/**
 * Kanonischer App-Katalog (nur Metadaten, 1:1 Spiegelung des IST-Zustands).
 *
 * Cross-Platform-Katalog (Web + Mobile-Hub): packages/services/src/platform/platformAppCatalog.js
 * Siehe docs/cross-platform-architecture.md
 *
 * Loader-Implementierungen bleiben bewusst in apps/web/src/modules/app-loader.js (APP_REGISTRY).
 * Diese Datei dokumentiert moduleSpecifier / supportsEmbed parallel dazu für spätere Zusammenführung.
 *
 * Abgeleitet aus u.a.:
 * - GlobalHubPage.jsx (APP_CARDS, Reihenfolge der Hub-Kacheln)
 * - apps/web/src/modules/app-loader.js (APP_REGISTRY, resolveAppKeyForRoute-Semantik)
 * - apps/web/src/reactApp.jsx (__appKeyForRoute-Semantik für Feature-Flags / Guards)
 * - apps/web/src/components/hub/hubIcons.js (Icons)
 * - apps/web/src/utils/governance/canAccessApp.js (ALL_APP_KEYS_PHASE1)
 * - apps/web/src/components/hub/RecentActivityList.jsx (recentActivityEligible)
 * - apps/web/src/modules/breadcrumb-registry.js (manifestAppId / Code-Fallbacks)
 *
 * Governance-/Admin-Routen sind hier nicht als Marketplace-Einträge modelliert — siehe DOCUMENTED_NON_CATALOG_ROUTES.
 */

/** @typedef {'dynamic-import' | 'none'} CatalogLoaderKind */

/** @typedef {'active' | 'beta' | 'hidden' | 'comingSoon'} CatalogAppStatus */

/** @typedef {'turniere' | 'training' | 'organisation' | 'tools'} MarketplaceCategory */

/**
 * Routen, die absichtlich keinen Marketplace-appKey tragen (Guards/Sonderlogik in reactApp.jsx).
 * Keine funktionale Änderung — nur Dokumentation für spätere shellRoute-Konsolidierung.
 */
export const DOCUMENTED_NON_CATALOG_ROUTES = Object.freeze([
  {
    routeSlug: "access-requests-admin",
    note: "Governance-Admin; canAccessAccessRequestsAdmin in reactApp.jsx",
  },
  {
    routeSlug: "accept-invite",
    note: "Governance-Einladung; öffentlich / halböffentlich je nach Flow",
  },
  {
    routeSlug: "feature-selection",
    note: "Feature-Auswahl / Access-Request-Einstieg",
  },
  {
    routeSlug: "access-pending",
    note: "Warteschlange ausstehende Zugriffs-/Registrierungsanfrage",
  },
  { routeSlug: "admin-people", note: "Verwaltung Kontakte (Hub-Verknüpfung)" },
  {
    routeSlug: "admin-people-detail",
    note: "Verwaltung Kontakt-Detail /verwaltung/kontakte/:id",
  },
  { routeSlug: "admin-locations", note: "Verwaltung Orte (Hub-Verknüpfung)" },
]);

/**
 * Statische Katalog-Einträge.
 * Reihenfolge der Marketplace-Apps entspricht GlobalHubPage APP_CARDS (TrainingsDesk → MatchMixer → …).
 *
 * @type {ReadonlyArray<{
 *   id?: string,
 *   appKey: string,
 *   shellRoute?: boolean,
 *   titleKey?: string,
 *   subtitleKey?: string,
 *   descriptionKey?: string|null,
 *   route?: string,
 *   routePrefixesDoc: string[],
 *   hubPath: string,
 *   showOnGlobalHub: boolean,
 *   bentoSize?: 'large'|'normal'|'wide',
 *   hubAccentEligible?: boolean,
 *   iconKey?: string|null,
 *   featureFlagKey: string,
 *   loader: { kind: CatalogLoaderKind, moduleSpecifier?: string, supportsEmbed?: boolean },
 *   status: CatalogAppStatus,
 *   marketplaceCategory: MarketplaceCategory,
 *   accessPolicyKey: string,
 *   manifestAppId?: string|null,
 *   inAllAppKeysPhase1?: boolean,
 *   recentActivityEligible?: boolean,
 *   shell?: {
 *     showAppHeader?: boolean,
 *     localNavPlacement?: 'content-top'|'below-global-header'|'none',
 *     preferredDesktopNav?: 'tabs'|'menu'|'none',
 *     preferredMobileNav?: 'sheet'|'tabs'|'none',
 *   },
 *   marketplace?: {
 *     visible?: boolean,
 *     categoryKey?: string|null,
 *   },
 *   notes?: string,
 * }>}
 *
 * Phase-1 „access-controlled“ App-Keys (canAccessApp ALL_APP_KEYS_PHASE1): `inAllAppKeysPhase1 === true`.
 * Nicht enthalten: share, settings (siehe Einträge mit inAllAppKeysPhase1: false + notes).
 *
 * Recent-Activity-Akzent/Matching (RecentActivityList): `recentActivityEligible === true` — IST nur
 * schleifchen, trainerstunden, clubmeisterschaft, medenspiele (nicht tennis-live/minigames).
 */
export const CATALOG_ENTRIES_STATIC = Object.freeze([
  {
    id: "trainerstunden",
    appKey: "trainerstunden",
    titleKey: "hub.trainerstunden.title",
    route: "/trainingsdesk/planung",
    routePrefixesDoc: [
      "trainingsdesk",
      "trainerstunden",
      "coachdesk",
      "trainerstunden-*",
    ],
    hubPath: "/trainingsdesk/planung",
    showOnGlobalHub: true,
    bentoSize: "normal",
    hubAccentEligible: true,
    iconKey: "graduation-cap",
    featureFlagKey: "trainerstunden",
    loader: {
      kind: "dynamic-import",
      moduleSpecifier: "@tennis-matches/trainerstunden",
      supportsEmbed: true,
    },
    status: "active",
    marketplaceCategory: "training",
    accessPolicyKey: "phase1-governance-derived",
    manifestAppId: "trainerstunden",
    inAllAppKeysPhase1: true,
    recentActivityEligible: true,
    shell: {
      showAppHeader: true,
      localNavPlacement: "content-top",
      preferredDesktopNav: "tabs",
      preferredMobileNav: "sheet",
    },
    marketplace: {
      visible: true,
      categoryKey: "marketplace.category.training",
    },
    notes:
      "Trainer-Login-Spezialfälle: GlobalHubPage + reactApp (allowedAppKeys).",
  },
  {
    id: "trainer",
    appKey: "trainer",
    titleKey: "hub.trainer.title",
    subtitleKey: "hub.trainer.subtitle",
    descriptionKey: "hub.trainer.subtitle",
    route: "/trainer",
    routePrefixesDoc: ["trainer", "trainer-*", "hub-trainer"],
    hubPath: "/hub/trainer",
    showOnGlobalHub: true,
    bentoSize: "normal",
    hubAccentEligible: true,
    iconKey: "clipboard-list",
    featureFlagKey: "trainer",
    loader: {
      kind: "dynamic-import",
      moduleSpecifier: "@tennis-matches/trainer",
      supportsEmbed: true,
    },
    status: "active",
    marketplaceCategory: "training",
    accessPolicyKey: "phase1-governance-derived",
    manifestAppId: null,
    inAllAppKeysPhase1: true,
    recentActivityEligible: false,
    shell: {
      showAppHeader: true,
      localNavPlacement: "content-top",
      preferredDesktopNav: "tabs",
      preferredMobileNav: "sheet",
    },
    marketplace: {
      visible: true,
      categoryKey: "marketplace.category.training",
    },
    notes:
      "Trainer Studio: Inhaltsplanung (Drills, Unterrichtspläne, Dokumentation). Unabhängig von TrainingsDesk; trainerstunden_session_id für spätere Verknüpfung.",
  },
  {
    id: "matchmixer",
    appKey: "matchmixer",
    titleKey: "hub.schleifchen.title",
    subtitleKey: "hub.schleifchen.subtitle",
    descriptionKey: "hub.schleifchen.subtitle",
    route: "/matchmixer",
    routePrefixesDoc: [
      "hub-matchmixer",
      "matchmixer",
      "matchmixer-*",
      "hub-schleifchen",
      "schleifchen",
      "schleifchen-*",
    ],
    hubPath: "/hub/matchmixer",
    showOnGlobalHub: true,
    bentoSize: "large",
    hubAccentEligible: true,
    iconKey: "repeat",
    featureFlagKey: "schleifchen",
    loader: {
      kind: "dynamic-import",
      moduleSpecifier: "@tennis-matches/matchmixer",
      supportsEmbed: true,
    },
    status: "active",
    marketplaceCategory: "turniere",
    accessPolicyKey: "phase1-governance-derived",
    manifestAppId: "matchmixer",
    inAllAppKeysPhase1: true,
    recentActivityEligible: true,
    shell: {
      showAppHeader: true,
      localNavPlacement: "content-top",
      preferredDesktopNav: "tabs",
      preferredMobileNav: "sheet",
    },
    marketplace: {
      visible: true,
      categoryKey: "marketplace.category.turniere",
    },
    notes:
      "reactApp: __appKeyForRoute mappt hub-schleifchen explizit; app-loader resolveAppKeyForRoute nutzt startsWith('schleifchen') (hub-schleifchen → kein Loader-Key).",
  },
  {
    id: "clubmeisterschaft",
    appKey: "clubmeisterschaft",
    titleKey: "hub.clubmeisterschaft.title",
    subtitleKey: "hub.clubmeisterschaft.subtitle",
    descriptionKey: "hub.clubmeisterschaft.subtitle",
    route: "/clubmeisterschaft",
    routePrefixesDoc: [
      "hub-clubmeisterschaft",
      "clubmeisterschaft",
      "clubmeisterschaft-*",
      "clubchamp",
      "clubchamp-*",
    ],
    hubPath: "/hub/clubmeisterschaft",
    showOnGlobalHub: true,
    bentoSize: "normal",
    hubAccentEligible: true,
    iconKey: "trophy",
    featureFlagKey: "clubmeisterschaft",
    loader: {
      kind: "dynamic-import",
      moduleSpecifier: "@tennis-matches/clubmeisterschaft-core/ui",
      supportsEmbed: true,
    },
    status: "active",
    marketplaceCategory: "turniere",
    accessPolicyKey: "phase1-governance-derived",
    manifestAppId: "clubmeisterschaft",
    inAllAppKeysPhase1: true,
    recentActivityEligible: true,
    shell: {
      showAppHeader: true,
      localNavPlacement: "content-top",
      preferredDesktopNav: "tabs",
      preferredMobileNav: "sheet",
    },
    marketplace: {
      visible: true,
      categoryKey: "marketplace.category.turniere",
    },
    notes:
      "APP_REGISTRY wrappt ClubmeisterschaftRoot als default; hub-clubmeisterschaft ist Hub-Shell-Route, kein DynamicAppLoader appKey aus Loader-Resolver.",
  },
  {
    id: "medenspiele",
    appKey: "medenspiele",
    titleKey: "hub.medenspiele.title",
    subtitleKey: "hub.medenspiele.subtitle",
    descriptionKey: "hub.medenspiele.subtitle",
    route: "/medenspiele",
    routePrefixesDoc: [
      "medenspiele",
      "medenspiele-*",
      "teamdesk",
      "teamdesk-*",
    ],
    hubPath: "/hub/medenspiele",
    showOnGlobalHub: true,
    bentoSize: "wide",
    hubAccentEligible: true,
    iconKey: "swords",
    featureFlagKey: "medenspiele",
    loader: {
      kind: "dynamic-import",
      moduleSpecifier: "@tennis-matches/medenspiele",
      supportsEmbed: true,
    },
    status: "active",
    marketplaceCategory: "turniere",
    accessPolicyKey: "phase1-governance-derived",
    manifestAppId: null,
    inAllAppKeysPhase1: true,
    recentActivityEligible: true,
    shell: {
      showAppHeader: true,
      localNavPlacement: "content-top",
      preferredDesktopNav: "tabs",
      preferredMobileNav: "sheet",
    },
    marketplace: {
      visible: true,
      categoryKey: "marketplace.category.turniere",
    },
    notes: "manifestAppId DB/Code kann abweichen — Breadcrumb-Registry hybrid.",
  },
  {
    id: "belegung",
    appKey: "belegung",
    titleKey: "hub.belegung.title",
    subtitleKey: "hub.belegung.subtitle",
    descriptionKey: "hub.belegung.subtitle",
    route: "/belegung",
    routePrefixesDoc: ["belegung", "belegung-*", "belegungsplan"],
    hubPath: "/hub/belegung",
    showOnGlobalHub: true,
    bentoSize: "wide",
    hubAccentEligible: true,
    iconKey: "calendar-days",
    featureFlagKey: "belegung",
    loader: {
      kind: "dynamic-import",
      moduleSpecifier: "@tennis-matches/belegung",
      supportsEmbed: true,
    },
    status: "active",
    marketplaceCategory: "organisation",
    accessPolicyKey: "phase1-governance-derived",
    manifestAppId: null,
    inAllAppKeysPhase1: true,
    recentActivityEligible: true,
    shell: {
      showAppHeader: true,
      localNavPlacement: "content-top",
      preferredDesktopNav: "none",
      preferredMobileNav: "none",
    },
    marketplace: {
      visible: true,
      categoryKey: "marketplace.category.organisation",
    },
    notes:
      "Platzbuchung / Belegungsplan; Domain in packages/services/belegung.",
  },
  {
    id: "kalender",
    appKey: "kalender",
    titleKey: "hub.kalender.title",
    subtitleKey: "hub.kalender.subtitle",
    descriptionKey: "hub.kalender.subtitle",
    route: "/kalender",
    routePrefixesDoc: ["kalender", "kalender-*"],
    hubPath: "/hub/kalender",
    showOnGlobalHub: true,
    bentoSize: "wide",
    hubAccentEligible: true,
    iconKey: "calendar-range",
    featureFlagKey: "kalender",
    loader: {
      kind: "dynamic-import",
      moduleSpecifier: "@tennis-matches/kalender",
      supportsEmbed: true,
    },
    status: "active",
    marketplaceCategory: "organisation",
    accessPolicyKey: "phase1-governance-derived",
    manifestAppId: null,
    inAllAppKeysPhase1: true,
    recentActivityEligible: true,
    shell: {
      showAppHeader: true,
      localNavPlacement: "content-top",
      preferredDesktopNav: "none",
      preferredMobileNav: "none",
    },
    marketplace: {
      visible: true,
      categoryKey: "marketplace.category.organisation",
    },
    notes:
      "Personenkalender; Quellen zuerst Serientermine + TrainingsDesk, später Turniere.",
  },
  {
    id: "insights",
    appKey: "insights",
    titleKey: "hub.insights.title",
    subtitleKey: "hub.insights.subtitle",
    descriptionKey: "hub.insights.subtitle",
    route: "/insights",
    routePrefixesDoc: ["insights", "insights-*", "statistik", "hub-insights"],
    hubPath: "/hub/insights",
    showOnGlobalHub: true,
    bentoSize: "wide",
    hubAccentEligible: true,
    iconKey: "bar-chart-3",
    featureFlagKey: "insights",
    loader: {
      kind: "dynamic-import",
      moduleSpecifier: "@tennis-matches/insights",
      supportsEmbed: true,
    },
    status: "active",
    marketplaceCategory: "organisation",
    accessPolicyKey: "phase1-governance-derived",
    manifestAppId: null,
    inAllAppKeysPhase1: true,
    recentActivityEligible: false,
    shell: {
      showAppHeader: true,
      localNavPlacement: "content-top",
      preferredDesktopNav: "none",
      preferredMobileNav: "none",
    },
    marketplace: {
      visible: true,
      categoryKey: "marketplace.category.organisation",
    },
    notes:
      "Club Insights: Cross-App-KPIs; Monetarisieren via features.clubInsights (Pro+).",
  },
  {
    id: "matchflow",
    appKey: "matchflow",
    titleKey: "hub.matchflow.title",
    subtitleKey: "hub.matchflow.subtitle",
    descriptionKey: "hub.matchflow.subtitle",
    route: "/matchflow",
    routePrefixesDoc: ["matchflow", "matchflow-*", "hub-matchflow"],
    hubPath: "/hub/matchflow",
    showOnGlobalHub: true,
    bentoSize: "wide",
    hubAccentEligible: true,
    iconKey: "workflow",
    featureFlagKey: "matchflow",
    loader: {
      kind: "dynamic-import",
      moduleSpecifier: "@tennis-matches/matchflow",
      supportsEmbed: true,
    },
    status: "active",
    marketplaceCategory: "organisation",
    accessPolicyKey: "staff-only-matchflow",
    manifestAppId: null,
    inAllAppKeysPhase1: false,
    recentActivityEligible: false,
    shell: {
      showAppHeader: true,
      localNavPlacement: "content-top",
      preferredDesktopNav: "none",
      preferredMobileNav: "none",
    },
    marketplace: {
      visible: false,
      categoryKey: "marketplace.category.organisation",
    },
    notes:
      "MatchFlow Ops: nur Superadmin / matchflow_staff — kein Vereins-Marketplace.",
  },
  {
    id: "winterdoppel",
    appKey: "winterdoppel",
    titleKey: "hub.winterdoppel.title",
    subtitleKey: "hub.winterdoppel.subtitle",
    descriptionKey: "hub.winterdoppel.subtitle",
    route: "/serientermine",
    routePrefixesDoc: [
      "serientermine",
      "serientermine-*",
      "regeltermine",
      "regeltermine-*",
      "winterdoppel",
      "winterdoppel-*",
    ],
    hubPath: "/hub/serientermine",
    showOnGlobalHub: true,
    bentoSize: "wide",
    hubAccentEligible: true,
    iconKey: "users",
    featureFlagKey: "winterdoppel",
    loader: {
      kind: "dynamic-import",
      moduleSpecifier: "@tennis-matches/winterdoppel",
      supportsEmbed: true,
    },
    status: "active",
    marketplaceCategory: "organisation",
    accessPolicyKey: "phase1-governance-derived",
    manifestAppId: null,
    inAllAppKeysPhase1: true,
    recentActivityEligible: true,
    shell: {
      showAppHeader: true,
      localNavPlacement: "content-top",
      preferredDesktopNav: "none",
      preferredMobileNav: "none",
    },
    marketplace: {
      visible: true,
      categoryKey: "marketplace.category.organisation",
    },
    notes: "Serientermine; Domain in packages/services/winterdoppel.",
  },
  {
    id: "clubhub",
    appKey: "clubhub",
    titleKey: "hub.clubhub.title",
    subtitleKey: "hub.clubhub.subtitle",
    descriptionKey: "hub.clubhub.subtitle",
    route: "/clubhub",
    routePrefixesDoc: ["clubhub", "clubhub-*"],
    hubPath: "/hub/clubhub",
    showOnGlobalHub: true,
    bentoSize: "normal",
    hubAccentEligible: true,
    iconKey: "building-2",
    featureFlagKey: "clubhub",
    loader: {
      kind: "none",
      supportsEmbed: false,
    },
    status: "active",
    marketplaceCategory: "organisation",
    accessPolicyKey: "phase1-governance-derived",
    manifestAppId: "clubhub",
    inAllAppKeysPhase1: true,
    recentActivityEligible: false,
    shell: {
      showAppHeader: true,
      localNavPlacement: "content-top",
      preferredDesktopNav: "none",
      preferredMobileNav: "none",
    },
    marketplace: {
      visible: true,
      categoryKey: "marketplace.category.organisation",
    },
    notes:
      "Vereinsverwaltung: Container-App für Kontakte, Orte, Usermanagement. Kein eigener Loader — rendert ClubHubPage mit Links zu bestehenden Verwaltungsseiten.",
  },
  {
    id: "minigames",
    appKey: "minigames",
    titleKey: "hub.minigames.title",
    subtitleKey: "hub.minigames.subtitle",
    descriptionKey: "hub.minigames.subtitle",
    route: "/minigames",
    routePrefixesDoc: ["minigames", "minigames-*"],
    hubPath: "/hub/minigames",
    showOnGlobalHub: true,
    bentoSize: "normal",
    hubAccentEligible: false,
    iconKey: null,
    featureFlagKey: "minigames",
    loader: {
      kind: "dynamic-import",
      moduleSpecifier: "@tennis-matches/minigames",
      supportsEmbed: true,
    },
    status: "active",
    marketplaceCategory: "tools",
    accessPolicyKey: "phase1-governance-derived",
    manifestAppId: null,
    inAllAppKeysPhase1: true,
    recentActivityEligible: false,
    shell: {
      showAppHeader: true,
      localNavPlacement: "content-top",
      preferredDesktopNav: "tabs",
      preferredMobileNav: "sheet",
    },
    marketplace: {
      visible: true,
      categoryKey: "marketplace.category.tools",
    },
    notes:
      "AppCard.jsx HUB_APP_KEYS enthält minigames nicht → hubAccentEligible false (IST). hubIcons: kein Eintrag für minigames. RecentActivityList: kein Akzent/Match (recentActivityEligible false).",
  },
  {
    id: "tennis-live",
    appKey: "tennis-live",
    titleKey: "hub.tenniswelt.title",
    subtitleKey: "hub.tenniswelt.subtitle",
    descriptionKey: "hub.tenniswelt.subtitle",
    route: "/tenniswelt",
    routePrefixesDoc: [
      "tennis-live",
      "tennis-live-*",
      "tenniswelt",
      "hub-tour",
    ],
    hubPath: "/hub/tenniswelt",
    showOnGlobalHub: true,
    bentoSize: "normal",
    hubAccentEligible: true,
    iconKey: "globe",
    featureFlagKey: "tennis-live",
    loader: {
      kind: "none",
      supportsEmbed: false,
    },
    status: "active",
    marketplaceCategory: "organisation",
    accessPolicyKey: "phase1-governance-derived",
    manifestAppId: null,
    inAllAppKeysPhase1: true,
    recentActivityEligible: false,
    shell: {
      showAppHeader: true,
      localNavPlacement: "content-top",
      preferredDesktopNav: "menu",
      preferredMobileNav: "sheet",
    },
    marketplace: {
      visible: true,
      categoryKey: "marketplace.category.organisation",
    },
    notes:
      "Kein APP_REGISTRY-Eintrag (Loader). Web-Shell: TennisweltPage unter /tenniswelt. Governance-Key bleibt tennis-live.",
  },
  {
    id: "share",
    appKey: "share",
    descriptionKey: null,
    route: "/share",
    routePrefixesDoc: ["share", "share-*"],
    hubPath: "/hub/share",
    showOnGlobalHub: false,
    hubAccentEligible: false,
    iconKey: null,
    featureFlagKey: "share",
    loader: {
      kind: "dynamic-import",
      moduleSpecifier: "@tennis-matches/share",
      supportsEmbed: true,
    },
    status: "active",
    marketplaceCategory: "tools",
    accessPolicyKey: "phase1-governance-derived",
    manifestAppId: null,
    inAllAppKeysPhase1: false,
    recentActivityEligible: false,
    shell: {
      showAppHeader: false,
      localNavPlacement: "none",
      preferredDesktopNav: "none",
      preferredMobileNav: "none",
    },
    marketplace: {
      visible: false,
      categoryKey: null,
    },
    notes:
      "APP_REGISTRY ja; GlobalHub APP_CARDS nein; ALL_APP_KEYS_PHASE1 nein; reactApp __appKeyForRoute mappt share nicht → kein Feature-Flag-Guard über appKey wie bei anderen Apps.",
  },
  {
    id: "settings",
    appKey: "settings",
    shellRoute: true,
    descriptionKey: null,
    route: "/hub/einstellungen",
    routePrefixesDoc: ["settings", "einstellungen", "hub-einstellungen"],
    hubPath: "/hub/einstellungen",
    showOnGlobalHub: false,
    hubAccentEligible: false,
    iconKey: null,
    featureFlagKey: "settings",
    loader: {
      kind: "none",
      supportsEmbed: false,
    },
    status: "active",
    marketplaceCategory: "organisation",
    accessPolicyKey: "shell-not-marketplace",
    manifestAppId: "settings",
    inAllAppKeysPhase1: false,
    recentActivityEligible: false,
    shell: {
      showAppHeader: false,
      localNavPlacement: "none",
      preferredDesktopNav: "none",
      preferredMobileNav: "none",
    },
    marketplace: {
      visible: false,
      categoryKey: null,
    },
    notes:
      "Einstellungen-Hub (/hub/einstellungen); Contributions via settingsContributions Registry. Legacy /settings und /hub/settings → gleiche Route.",
  },
]);
